import type { PublicMaterial } from '@/types';

interface MaterialCardProps {
  material: PublicMaterial;
  isSelected: boolean;
  onSelect: (materialId: string) => void;
  onDeselect: (materialId: string) => void;
  onPreview?: (material: PublicMaterial) => void;
}

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20MB

/**
 * Displays a marketing material card with thumbnail, headline, description, and file size
 * Shows a warning badge for files >20MB and disables selection
 */
export default function MaterialCard({ material, isSelected, onSelect, onDeselect, onPreview }: MaterialCardProps) {
  const isOversized = material.fileSizeBytes > MAX_FILE_SIZE_BYTES;
  const fileSizeMB = (material.fileSizeBytes / (1024 * 1024)).toFixed(2);

  const handleToggle = () => {
    if (isOversized) return;
    
    if (isSelected) {
      onDeselect(material.documentId);
    } else {
      onSelect(material.documentId);
    }
  };

  const handlePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPreview?.(material);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <div
      className={`
        border rounded-lg p-4 cursor-pointer transition-all
        ${isSelected ? 'border-brand bg-brand/5 ring-2 ring-brand' : 'border-gray-200 hover:border-gray-300'}
        ${isOversized ? 'opacity-60 cursor-not-allowed' : ''}
      `}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={isOversized ? -1 : 0}
      aria-pressed={isSelected}
      aria-label={`${material.headline} - ${fileSizeMB} MB ${material.fileType} file${isOversized ? ' (exceeds size limit)' : ''}${isSelected ? ' - selected' : ''}`}
      aria-disabled={isOversized}
    >
      <div className="flex gap-3">
        {/* Thumbnail */}
        <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded border flex items-center justify-center overflow-hidden">
          {material.thumbnailUrl ? (
            <img 
              src={material.thumbnailUrl} 
              alt={material.headline} 
              className="w-full h-full object-cover"
            />
          ) : (
            <FileIcon fileType={material.fileType} />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-semibold text-sm truncate">{material.headline}</h4>
            <div className="flex items-center gap-1 flex-shrink-0">
              {onPreview && (
                <button
                  onClick={handlePreview}
                  className="p-1 text-gray-400 hover:text-brand transition-colors rounded"
                  aria-label="Preview material"
                  title="Preview"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              )}
              {isSelected && !isOversized && (
                <CheckIcon className="w-5 h-5 text-brand" />
              )}
            </div>
          </div>
          
          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{material.description}</p>
          
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-gray-500 uppercase">{material.fileType}</span>
            <span className="text-xs text-gray-400">•</span>
            <span className={`text-xs ${isOversized ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
              {fileSizeMB} MB
            </span>
            {isOversized && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">
                Exceeds 20MB limit
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * File type icon component - displays appropriate icon based on file extension
 */
function FileIcon({ fileType }: { fileType: string }) {
  const iconMap: Record<string, string> = {
    pdf: '📄',
    doc: '📝',
    docx: '📝',
    ppt: '📊',
    pptx: '📊',
    png: '🖼️',
    jpg: '🖼️',
    jpeg: '🖼️',
  };

  return (
    <div className="text-3xl">
      {iconMap[fileType.toLowerCase()] || '📎'}
    </div>
  );
}

/**
 * Checkmark icon component for selected state
 */
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg 
      className={className}
      fill="none" 
      viewBox="0 0 24 24" 
      stroke="currentColor"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M5 13l4 4L19 7" 
      />
    </svg>
  );
}
